import { graphql } from "gatsby"
import kebabCase from "lodash.kebabcase"
import useTranslations from "hooks/useTranslations"

import LocalizedLink from "components/LocalizedLink"
import SEO from "components/seo"
import IconDocument from "images/icon-document.svg"

const Description = ({ children }) => {
  return <div className="prose prose-sm mt-1 no-underline">{children}</div>
}

const Card = ({ children }) => {
  return (
    <div className="relative flex flex-col rounded-lg border-2 border-teal-500 shadow-lg">
      <div className="flex flex-1 flex-col justify-between p-6">{children}</div>
    </div>
  )
}

const LegalTemplate = ({ data }) => {
  const { legalPageName } = useTranslations()

  const legalDocsList = Object.values(data.allPrismicLegal.nodes).reduce(
    (acc, node) => {
      const displayedName = node.data.displayed_name.text
      const description = node.data.document_description?.text
      const urlSlug = kebabCase(node.data.slug.text)
      const slug = `/legal/${urlSlug}`

      // UK pilot docs (URL slugs starting with "uk-") live on somebuddy.com
      // but are intentionally hidden from this listing — accessed only via
      // direct link from within the app. Convention agreed 2026-05-11;
      // coordinate URL slug naming with toosa + peikko UK link constants
      // (LEGAL_PAGE_LINK_EN_GB, PRIVACY_POLICY_LINK_EN_GB, etc).
      // Filter on the kebab-cased URL slug rather than the raw Prismic
      // title — that's what's stable across Prismic-side title rewrites
      // and matches the URLs the apps reference.
      const isUkDoc = urlSlug.startsWith("uk-")
      const isOnlineShopTerms = urlSlug === "online-shop-terms-and-conditions"

      if (!isOnlineShopTerms && !isUkDoc) {
        acc.push({
          displayedName,
          slug,
          description,
        })
      }

      return acc
    },
    [],
  )

  const menuLegalLinks = legalDocsList.map(
    ({ displayedName, slug, description }) => ({
      title: displayedName,
      link: slug,
      description,
    }),
  )

  return (
    <>
      <SEO title={legalPageName} />
      <div className="relative mt-16 flex w-full flex-1 bg-white px-4 py-16 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8 xl:mx-auto">
        <div className="prose max-w-screen-lg space-y-4 text-lg">
          <h1 className="">{legalPageName}</h1>
          <div className="relative mx-auto max-w-7xl">
            <div className="mx-auto mt-12 grid gap-5 lg:grid-cols-2">
              {menuLegalLinks.map((item) => (
                <LocalizedLink to={item.link}>
                  <Card href={item.link}>
                    <div className="prose prose-teal items-start hover:text-teal-500">
                      <IconDocument className="absolute -left-2 -top-2 mt-1 w-8 bg-white text-teal-800" />
                      <a>{item.title}</a>
                      <div className="flex-1 font-normal">
                        {item.description ? (
                          <Description>{item.description}</Description>
                        ) : null}
                      </div>
                    </div>
                  </Card>
                </LocalizedLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default LegalTemplate

export const pageQuery = graphql`
  query ($locale: String!) {
    allPrismicLegal(
      sort: { data: { index: ASC } }
      filter: { lang: { eq: $locale } }
    ) {
      nodes {
        data {
          displayed_name {
            text
          }
          slug {
            text
          }
          document_description {
            text
          }
        }
      }
    }
  }
`
